import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Circuit Stats Widget
 * Circuit details with lap records and track information
 * Shows circuit specifications, lap records, and historical data
 */
const F1CircuitStats = ({ season = 2025 }) => {
  const [circuits, setCircuits] = useState([]);
  const [selectedCircuit, setSelectedCircuit] = useState(null);
  const [circuitDetails, setCircuitDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  // Fetch all circuits
  useEffect(() => {
    const fetchCircuits = async () => {
      try {
        setLoading(true);
        const response = await f1Service.getAllCircuits();

        if (response && response.response) {
          // Sort circuits alphabetically
          const sortedCircuits = response.response.sort((a, b) => {
            return a.name.localeCompare(b.name);
          });
          setCircuits(sortedCircuits);

          // Auto-select first circuit
          if (sortedCircuits.length > 0) {
            setSelectedCircuit(sortedCircuits[0].id);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching circuits:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCircuits();
  }, [season]);

  // Fetch selected circuit details
  useEffect(() => {
    if (!selectedCircuit) return;

    const fetchCircuitDetails = async () => {
      try {
        const response = await f1Service.getCircuitById(selectedCircuit);
        if (response && response.response && response.response.length > 0) {
          setCircuitDetails(response.response[0]);
        }
      } catch (err) {
        console.error('Error fetching circuit details:', err);
      }
    };

    fetchCircuitDetails();
  }, [selectedCircuit]);

  // Format length
  const formatLength = (length) => {
    if (!length) return 'Unknown';
    // Length is typically in meters or km string
    return length;
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4">
          <div className="h-64 bg-white/10 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (circuits.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Circuit Data Available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono mb-3">
          Circuit Statistics
        </h3>

        {/* Circuit Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider whitespace-nowrap">
            Circuit:
          </span>
          <div className="flex gap-2">
            {circuits.map((circuit) => (
              <button
                key={circuit.id}
                onClick={() => setSelectedCircuit(circuit.id)}
                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-all duration-150 border whitespace-nowrap ${
                  selectedCircuit === circuit.id
                    ? 'bg-white/10 text-white border-white/20'
                    : 'bg-transparent text-gray-600 border-white/5 hover:border-white/10'
                }`}
              >
                {circuit.name.split(' ')[0]} {/* Show first word of circuit name */}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Circuit Details */}
      {circuitDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Circuit Image (if available) */}
          {circuitDetails.image && (
            <div className="relative h-48 overflow-hidden border-b border-white/5">
              <img
                src={circuitDetails.image}
                alt={circuitDetails.name}
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              {/* Circuit Name Overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-lg font-bold text-white uppercase tracking-tight">
                  {circuitDetails.name}
                </div>
                {circuitDetails.competition?.location && (
                  <div className="text-xs text-gray-400 font-mono mt-1">
                    {circuitDetails.competition.location.city}, {circuitDetails.competition.location.country}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Circuit Information */}
          <div className="p-4 space-y-4">
            {/* Basic Info */}
            <div className="space-y-3">
              <div className="text-[10px] text-gray-600 uppercase tracking-wider font-bold mb-2">
                Track Information
              </div>

              {/* Name (if no image) */}
              {!circuitDetails.image && (
                <div className="pb-3 border-b border-white/5">
                  <div className="text-sm font-bold text-white">
                    {circuitDetails.name}
                  </div>
                  {circuitDetails.competition?.location && (
                    <div className="text-xs text-gray-400 font-mono mt-1">
                      {circuitDetails.competition.location.city}, {circuitDetails.competition.location.country}
                    </div>
                  )}
                </div>
              )}

              {/* Track Specs Grid */}
              <div className="grid grid-cols-2 gap-4 text-xs font-mono">
                {/* Length */}
                <div className="p-3 bg-white/[0.02] border border-white/5 rounded-sm">
                  <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">
                    Length
                  </div>
                  <div className="text-white font-bold">
                    {formatLength(circuitDetails.length)}
                  </div>
                </div>

                {/* Capacity */}
                {circuitDetails.capacity && (
                  <div className="p-3 bg-white/[0.02] border border-white/5 rounded-sm">
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">
                      Capacity
                    </div>
                    <div className="text-white font-bold">
                      {parseInt(circuitDetails.capacity).toLocaleString()}
                    </div>
                  </div>
                )}

                {/* Opened */}
                {circuitDetails.opened && (
                  <div className="p-3 bg-white/[0.02] border border-white/5 rounded-sm">
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">
                      Opened
                    </div>
                    <div className="text-white font-bold">
                      {circuitDetails.opened}
                    </div>
                  </div>
                )}

                {/* Owner */}
                {circuitDetails.owner && (
                  <div className="p-3 bg-white/[0.02] border border-white/5 rounded-sm">
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">
                      Owner
                    </div>
                    <div className="text-white font-bold truncate">
                      {circuitDetails.owner}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Lap Record (if available) */}
            {circuitDetails.lap_record && (
              <div className="space-y-2 pt-3 border-t border-white/5">
                <div className="text-[10px] text-gray-600 uppercase tracking-wider font-bold">
                  Lap Record
                </div>
                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-white font-semibold">
                      {circuitDetails.lap_record.driver || 'Unknown Driver'}
                    </div>
                    <div className="text-sm text-purple-400 font-bold font-mono tabular-nums">
                      {circuitDetails.lap_record.time || '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-gray-500 font-mono">
                    {circuitDetails.lap_record.team && (
                      <span>{circuitDetails.lap_record.team}</span>
                    )}
                    {circuitDetails.lap_record.year && (
                      <span>{circuitDetails.lap_record.year}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Race History (if available) */}
            {circuitDetails.first_grand_prix && (
              <div className="space-y-2 pt-3 border-t border-white/5">
                <div className="text-[10px] text-gray-600 uppercase tracking-wider font-bold">
                  Race History
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                  <div className="flex justify-between">
                    <span className="text-gray-500">First GP</span>
                    <span className="text-white font-bold">{circuitDetails.first_grand_prix}</span>
                  </div>
                  {circuitDetails.number_of_laps && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Laps</span>
                      <span className="text-white font-bold">{circuitDetails.number_of_laps}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Race Distance (calculated) */}
            {circuitDetails.race_distance && (
              <div className="pt-3 border-t border-white/5">
                <div className="flex justify-between text-xs font-mono">
                  <span className="text-gray-500 uppercase tracking-wider">Race Distance</span>
                  <span className="text-white font-bold">{circuitDetails.race_distance}</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <span>Total Circuits</span>
          <span className="text-white font-bold">{circuits.length}</span>
        </div>
      </div>
    </div>
  );
};

export default F1CircuitStats;
